import React, { FC, useEffect } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { PackageSearchSelectListItem } from "src/components/PackageSearchSelectListItem";
import { FormInput, registerObject } from "go-form";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ReactComponent as PackageSVG } from "../../../../../../../../../images/svg/products/package.svg";
import {
	ProductApi,
	ProductComponentApi,
	ProductRecipeApi,
} from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";

interface Props {
	form: UseFormReturn<ProductApi>;
	recipeIndex: number;
	watchedRecipes: ProductRecipeApi[];
}

const ProductRecipeItemsForm: FC<Props> = ({ form, recipeIndex, watchedRecipes }) => {
	const { t } = useTranslation();
	const {
		watch,
		control,
		register,
		getValues,
		formState: { errors },
		setValue,
	} = form;
	const { remove } = useFieldArray({
		control,
		name: `recipes.${recipeIndex}.components`,
		keyName: "key",
	});
	const organization = useSelector(selectOrganization);
	const currency = organization?.currency || "PLN";
	const watchedComponents = watchedRecipes[recipeIndex]?.components;
	const componentsAsString = JSON.stringify(watchedComponents);
	const { isMobile } = useWindowSize();

	const checkIfAddNewItem = () => {
		if (!watchedComponents) return true;
		const prevItem = watchedComponents[watchedComponents?.length - 1];
		return !!prevItem?.product_id || !!prevItem?.recipe_amount;
	};

	useEffect(() => {
		if (checkIfAddNewItem()) {
			setTimeout(() => {
				const componentIndex = watchedComponents ? watchedComponents.length : 0;
				setValue(
					`recipes.${recipeIndex}.components.${componentIndex}`,
					{
						id: new Date().getTime(),
						product_id: null,
						recipe_amount: null,
						product_name: "",
						measure_type: "ITEM",
						product_cost_gross_money: null,
						product_cost_money: null,
					} as unknown as ProductComponentApi,
					{ shouldDirty: false }
				);
			}, 1);
		}
	}, [componentsAsString]);

	const onSearchProduct = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const { id: productId } = watch();

		const currentSelectedRecipesIds = watch("recipes")
			?.[recipeIndex].components.filter((recipe) => recipe.product_id)
			.map((recipe) => recipe.product_id)
			.join(FILTER_VALUE_SEPARATOR);

		let resourceIdsToBeExcluded = "";

		if (currentSelectedRecipesIds && productId) {
			resourceIdsToBeExcluded = `${currentSelectedRecipesIds}${FILTER_VALUE_SEPARATOR}${productId}`;
		} else if (currentSelectedRecipesIds && !productId) {
			resourceIdsToBeExcluded = currentSelectedRecipesIds;
		} else if (!currentSelectedRecipesIds && productId) {
			resourceIdsToBeExcluded = productId.toString();
		}

		const newParams = { ...params, "ids|ne": resourceIdsToBeExcluded };

		return api.organization().getProductSearchSelect(search, newParams, {
			cancelToken: options?.token,
		});
	};

	const onChangeProduct = async (product: ProductApi, index: number) => {
		if (!product) return;

		registerObject(register, `recipes.${recipeIndex}.components.product`, [
			"measure_type_unit",
			"name",
			"id",
			"cost_price_gross_money",
			"cost_price_money",
			"sub_product_type",
		]);
		setValue(`recipes.${recipeIndex}.components.${index}.product_name`, product.name);
		setValue(`recipes.${recipeIndex}.components.${index}.product_cost_gross_money`, product.cost_price_gross_money);
		setValue(`recipes.${recipeIndex}.components.${index}.product_cost_money`, product.cost_price_money);
		setValue(`recipes.${recipeIndex}.components.${index}.measure_type`, product.measure_type);
		setValue(`recipes.${recipeIndex}.components.${index}.product_id`, product.id);
		setValue(`recipes.${recipeIndex}.components.${index}.sub_product_type`, product.type);
	};

	const getTotalCost = (index: number) => {
		const recipeAmount = watch(`recipes.${recipeIndex}.components.${index}.recipe_amount`) || 0;
		const unitProductCostMoney = getValues(`recipes.${recipeIndex}.components.${index}.unit_product_cost_money`);
		const unitCost = unitProductCostMoney
			? unitProductCostMoney.amount
			: getValues(`recipes.${recipeIndex}.components.${index}.product_cost_money.amount`) || 0;

		const unitProductGrossCostMoney = getValues(
			`recipes.${recipeIndex}.components.${index}.unit_product_cost_gross_money`
		);
		const grossCost = unitProductGrossCostMoney
			? unitProductGrossCostMoney.amount
			: getValues(`recipes.${recipeIndex}.components.${index}.product_cost_gross_money.amount`) || 0;

		return {
			totalCost: recipeAmount * unitCost,
			totalGrossCost: recipeAmount * grossCost,
		};
	};

	const drawCost = (index: number) => {
		return `${FormatMoney({
			amount: getTotalCost(index).totalCost,
			currency,
		})} (${FormatMoney({ amount: getTotalCost(index).totalGrossCost, currency })})`;
	};

	const onRemove = (index: number) => {
		remove(index);
	};

	const totalCost = watchedComponents?.map((_, i) => getTotalCost(i).totalCost).reduce((acc, cur) => acc + cur, 0);
	const totalGrossCost = watchedComponents
		?.map((_, i) => getTotalCost(i).totalGrossCost)
		.reduce((acc, cur) => acc + cur, 0);

	return (
		<div className={isMobile ? "table-responsive" : ""}>
			<table className={`table table-form positions-table ${isMobile ? "vertical-top" : ""}`}>
				<thead>
					<tr>
						<th className="action" />
						<th className="w-50" style={{ minWidth: isMobile ? "200px" : "" }}>
							{t("lib:common.word.name")} *
						</th>
						<th className="w-15" style={{ minWidth: isMobile ? "50px" : "" }}>
							{t("common.word.measure")}
						</th>
						<th className="w-15" style={{ minWidth: isMobile ? "100px" : "" }}>
							{t("common.word.quantity")} *
						</th>
						<th className="w-20" style={{ minWidth: isMobile ? "150px" : "" }}>
							{t("common.word.cost")} ({t("modules.product.field.gross_cost.title")})
						</th>
						<th className="action" />
					</tr>
				</thead>
				<tbody>
					{watchedComponents?.map((item, index) => (
						<tr key={`tr-${item?.id}`}>
							<td className="action" />
							<td>
								<>
									{item?.product_id ? (
										<div className="d-flex align-items-center">
											<Link
												target="_blank"
												to={`/${organization.id}/products/${item.product_id}/edit`}
											>
												{item?.product_name}
											</Link>
											{item?.sub_product_type === "PACKAGE" && <PackageSVG className="ms-2" />}
										</div>
									) : (
										<FormSelectGroup
											name={`recipes.${recipeIndex}.components.${index}.product_name`}
											key={`product-name-${recipeIndex}`}
											errors={errors}
											onChange={(id, product) => onChangeProduct(product, index)}
											defaultValue={{
												label: item?.product_name,
												id: item?.product_id,
											}}
											getOptionLabel={(option) => {
												return option.product_type === "PACKAGE" ? (
													<PackageSearchSelectListItem label={option.label} />
												) : (
													option.label
												);
											}}
											loadOptions={onSearchProduct}
											control={control}
											data-testid={`recipes.${recipeIndex}.components.${index}.product_name`}
										/>
									)}
									<FormInput
										type="hidden"
										errors={errors}
										register={register}
										key={`product-id-${recipeIndex}`}
										name={`recipes.${recipeIndex}.components.${index}.product_id`}
										defaultValue={item.product_id}
									/>
								</>
							</td>
							<td>{item?.measure_type && t(`enums.products.measure_types.${item.measure_type}`)}</td>
							<td>
								<FormNumberInput
									errors={errors}
									control={control}
									key={`recipe-amount-${recipeIndex}`}
									defaultValue={item.recipe_amount || null}
									decimalScale={5}
									name={`recipes.${recipeIndex}.components.${index}.recipe_amount`}
									suffix=""
								/>
							</td>
							<td>{drawCost(index)}</td>
							<td className="action">
								{!(
									(watchedComponents.length === 1 && index === 0) ||
									index === watchedComponents.length - 1
								) && <RemoveSVG className="icon" onClick={() => onRemove(index)} />}
							</td>
						</tr>
					))}
					<tr className="summary">
						{!isMobile && <td className="action" />}
						<td colSpan={isMobile ? 0 : 2} />
						<td
							className={`${isMobile ? "text-start" : "text-end"} text-nowrap`}
							colSpan={isMobile ? 0 : 2}
						>
							<strong>
								{`${t("common.word.total_cost")}: ${FormatMoney({
									amount: totalCost,
									currency,
								})} (${FormatMoney({ amount: totalGrossCost, currency })})`}
							</strong>
						</td>
						{!isMobile && <td className="action" />}
					</tr>
				</tbody>
			</table>
		</div>
	);
};

export default ProductRecipeItemsForm;
